import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { X, Save, User } from 'lucide-react';

const TechnicianForm = ({ isOpen, onClose, onSuccess, editingTechnician = null }) => {
  const { currentUser, userRole } = useAuth();
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    domicilio: '',
    email: '',
    fechaNacimiento: '',
    fechaIngreso: '',
    fechaBaja: '',
    isArchived: false
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Populate form when editing
  useEffect(() => {
    if (editingTechnician) {
      const formatDateForInput = (date) => {
        if (!date) return '';
        const dateObj = date.toDate ? date.toDate() : new Date(date);
        return dateObj.toISOString().split('T')[0];
      };

      setFormData({
        nombre: editingTechnician.nombre || '',
        telefono: editingTechnician.telefono || '',
        domicilio: editingTechnician.domicilio || '',
        email: editingTechnician.email || '',
        fechaNacimiento: formatDateForInput(editingTechnician.fechaNacimiento),
        fechaIngreso: formatDateForInput(editingTechnician.fechaIngreso),
        fechaBaja: formatDateForInput(editingTechnician.fechaBaja),
        isArchived: editingTechnician.isArchived || false
      });
    } else {
      // Reset form for new technician
      setFormData({
        nombre: '',
        telefono: '',
        domicilio: '',
        email: '',
        fechaNacimiento: '',
        fechaIngreso: '',
        fechaBaja: '',
        isArchived: false
      });
    }
    setErrors({});
    setSubmitError('');
  }, [editingTechnician, isOpen]);

  const checkPhoneUniqueness = async (phone) => {
    try {
      const cleanPhone = phone.replace(/\s/g, '');
      const q = query(
        collection(db, 'technicians'), 
        where('telefono', '==', cleanPhone)
      );
      const querySnapshot = await getDocs(q);
      
      // If editing, exclude the current technician from the check
      if (editingTechnician) {
        return querySnapshot.docs.some(doc => doc.id !== editingTechnician.id);
      }
      
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking phone uniqueness:', error);
      return false; // Assume unique if there's an error
    }
  };

  const validateForm = async () => {
    const newErrors = {};

    // Required field validations
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!formData.telefono.trim()) {
      newErrors.telefono = 'El teléfono es requerido';
    } else if (!/^\d{10}$/.test(formData.telefono.replace(/\s/g, ''))) {
      newErrors.telefono = 'El teléfono debe tener exactamente 10 dígitos';
    } else {
      // Check phone uniqueness
      const phoneExists = await checkPhoneUniqueness(formData.telefono);
      if (phoneExists) {
        newErrors.telefono = 'Este número de teléfono ya está registrado para otro técnico';
      }
    }

    if (!formData.domicilio.trim()) {
      newErrors.domicilio = 'El domicilio es requerido';
    }

    if (!formData.fechaNacimiento) {
      newErrors.fechaNacimiento = 'La fecha de nacimiento es requerida';
    }

    if (!formData.fechaIngreso) {
      newErrors.fechaIngreso = 'La fecha de ingreso es requerida';
    }

    // Email validation (optional but must be valid if provided)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El formato del email no es válido';
    }

    // Date validation - fechaNacimiento should be before fechaIngreso
    if (formData.fechaNacimiento && formData.fechaIngreso) {
      const birthDate = new Date(formData.fechaNacimiento);
      const entryDate = new Date(formData.fechaIngreso);
      
      if (birthDate >= entryDate) {
        newErrors.fechaNacimiento = 'La fecha de nacimiento debe ser anterior a la fecha de ingreso';
      }

      // fechaIngreso shouldn't be in the future
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      if (entryDate > today) {
        newErrors.fechaIngreso = 'La fecha de ingreso no puede ser futura';
      }
    }

    // fechaBaja validation (if provided, should be after fechaIngreso)
    if (formData.fechaBaja && formData.fechaIngreso) {
      const entryDate = new Date(formData.fechaIngreso);
      const exitDate = new Date(formData.fechaBaja);
      
      if (exitDate < entryDate) {
        newErrors.fechaBaja = 'La fecha de baja debe ser posterior a la fecha de ingreso';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    setLoading(true);
    setSubmitError('');

    try {
      // Check if user has permission to create/edit technicians
      const action = editingTechnician ? 'editar' : 'crear';
      if (!userRole || !['admin', 'shopkeeper'].includes(userRole)) {
        setSubmitError(`No tienes permisos para ${action} técnicos. Tu rol actual es: ${userRole || 'sin rol'}`);
        setLoading(false);
        return;
      }
      
      // Prepare data for Firestore
      const technicianData = {
        nombre: formData.nombre.trim(),
        telefono: formData.telefono.replace(/\s/g, ''), // Remove spaces
        domicilio: formData.domicilio.trim(),
        email: formData.email.trim() || null,
        fechaNacimiento: new Date(formData.fechaNacimiento),
        fechaIngreso: new Date(formData.fechaIngreso),
        fechaBaja: formData.fechaBaja ? new Date(formData.fechaBaja) : null,
        isArchived: formData.isArchived,
        updatedAt: new Date()
      };

      if (editingTechnician) {
        // Update existing technician
        await updateDoc(doc(db, 'technicians', editingTechnician.id), technicianData);
      } else {
        // Create new technician
        technicianData.createdAt = new Date();
        await addDoc(collection(db, 'technicians'), technicianData);
      }
      
      // Reset form and close
      setFormData({
        nombre: '',
        telefono: '',
        domicilio: '',
        email: '',
        fechaNacimiento: '',
        fechaIngreso: '',
        fechaBaja: '',
        isArchived: false
      });
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating technician:', error);
      setSubmitError('Error al guardar el técnico. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form when closing
      setFormData({
        nombre: '',
        telefono: '',
        domicilio: '',
        email: '',
        fechaNacimiento: '',
        fechaIngreso: '',
        fechaBaja: '',
        isArchived: false
      });
      setErrors({});
      setSubmitError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <User className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingTechnician ? 'Editar Técnico' : 'Nuevo Técnico'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 bg-white dark:bg-gray-800">
          {submitError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md mb-6">
              {submitError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nombre */}
            <div className="md:col-span-2">
              <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nombre *
              </label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                  errors.nombre ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Nombre completo del técnico"
              />
              {errors.nombre && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.nombre}</p>}
            </div>

            {/* Teléfono */}
            <div>
              <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Teléfono *
              </label>
              <input
                type="tel"
                id="telefono"
                name="telefono"
                value={formData.telefono}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                  errors.telefono ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="1234567890"
                maxLength="10"
              />
              {errors.telefono && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.telefono}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                  errors.email ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="email@ejemplo.com"
              />
              {errors.email && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>}
            </div>

            {/* Domicilio */}
            <div className="md:col-span-2">
              <label htmlFor="domicilio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Domicilio *
              </label>
              <textarea
                id="domicilio"
                name="domicilio"
                value={formData.domicilio}
                onChange={handleInputChange}
                rows="2"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                  errors.domicilio ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Dirección completa del técnico"
              />
              {errors.domicilio && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.domicilio}</p>}
            </div>

            {/* Fecha de Nacimiento */}
            <div>
              <label htmlFor="fechaNacimiento" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Fecha de Nacimiento *
              </label>
              <input
                type="date"
                id="fechaNacimiento"
                name="fechaNacimiento"
                value={formData.fechaNacimiento}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                  errors.fechaNacimiento ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {errors.fechaNacimiento && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.fechaNacimiento}</p>}
            </div>

            {/* Fecha de Ingreso */}
            <div>
              <label htmlFor="fechaIngreso" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Fecha de Ingreso *
              </label>
              <input
                type="date"
                id="fechaIngreso"
                name="fechaIngreso"
                value={formData.fechaIngreso}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                  errors.fechaIngreso ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {errors.fechaIngreso && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.fechaIngreso}</p>}
            </div>

            {/* Fecha de Baja */}
            <div>
              <label htmlFor="fechaBaja" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Fecha de Baja
              </label>
              <input
                type="date"
                id="fechaBaja"
                name="fechaBaja"
                value={formData.fechaBaja}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                  errors.fechaBaja ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {errors.fechaBaja && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.fechaBaja}</p>}
            </div>

            {/* Is Archived */}
            <div>
              <div className="flex items-center mt-6">
                <input
                  type="checkbox"
                  id="isArchived"
                  name="isArchived"
                  checked={formData.isArchived}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-orange-600 dark:text-orange-400 focus:ring-orange-500 dark:focus:ring-orange-400 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                />
                <label htmlFor="isArchived" className="ml-2 block text-sm text-gray-900 dark:text-white">
                  Marcar como archivado
                </label>
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-600 dark:bg-orange-500 text-white rounded-md hover:bg-orange-700 dark:hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Guardando...' : (editingTechnician ? 'Actualizar Técnico' : 'Guardar Técnico')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TechnicianForm;